/**
 * Platform Admin Management Component
 * Platform admin UI for managing other platform administrators
 */

import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../config/environment';


interface PlatformAdminManagementProps {
  accessToken: string;
}

interface PlatformAdmin {
  adminId: string;
  email: string;
  displayName: string;
  userId?: string;
  grantedBy: string;
  grantedAt: string;
  notes?: string;
  isActive: boolean;
  lastActiveAt?: string;
}

export const PlatformAdminManagement: React.FC<PlatformAdminManagementProps> = ({
  accessToken
}) => {
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAdmins();
  }, [accessToken]);

  const loadAdmins = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/platform/admins`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load platform admins: ${response.statusText}`);
      }

      const data = await response.json();
      setAdmins(data.admins || []);
    } catch (err: any) {
      console.error('Error loading platform admins:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const thCls = 'px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider';
  const tdCls = 'px-4 py-3 text-label text-gray-700';

  if (isLoading) return <div className="py-12 text-center text-body text-gray-400">Loading platform administrators…</div>;

  if (error) return (
    <div className="py-12 text-center">
      <p className="text-body text-error mb-4">Error: {error}</p>
      <button type="button" onClick={loadAdmins}
        className="px-4 py-2 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast">
        Retry
      </button>
    </div>
  );

  return (
    <div className="w-full">
      <h2 className="text-xl font-semibold text-gray-900 mb-5">Platform Administrators</h2>

      {admins.length === 0 ? (
        <div className="py-16 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 className="text-body font-semibold text-gray-700 mb-1">No Platform Admins Found</h3>
          <p className="text-label text-gray-400">Platform admins are configured via environment variables.</p>
        </div>
      ) : (
        <table className="w-full bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className={thCls}>Email</th><th className={thCls}>Display Name</th>
              <th className={thCls}>Granted By</th><th className={thCls}>Granted At</th><th className={thCls}>Status</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.adminId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-fast">
                <td className={`${tdCls} font-mono text-meta`}>{admin.email}</td>
                <td className={tdCls}>{admin.displayName || '—'}</td>
                <td className={`${tdCls} font-mono text-meta`}>{admin.grantedBy}</td>
                <td className={tdCls}>{new Date(admin.grantedAt).toLocaleDateString()}</td>
                <td className={tdCls}>
                  <span className={`px-2.5 py-0.5 rounded-full text-meta font-semibold ${admin.isActive ? 'bg-success-light text-success-dark' : 'bg-gray-100 text-gray-500'}`}>
                    {admin.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

